import { useEffect, useRef, useState } from "hono/jsx";

interface MapControl {
  highlightCountryWithLabel: (
    iso2: string,
    label: string,
    color?: string,
    type?: "target" | "guess",
  ) => void;
  setHardMode: (mode: boolean) => void;
  setUseHighRes: (use: boolean) => void;
  resetHighlights: () => void;
  getCountryCenter: (iso2: string) => [number, number] | null;
  panToCountry: (iso2: string) => void;
  getBounds: () => any;
  fitBounds: (bounds: [[number, number], [number, number]]) => void;
  resetView: () => void;
  getData: () => any;
  destroy: () => void;
}

const CONTINENTS = [
  { name: "North America", bounds: [[15, -170], [75, -50]] },
  { name: "South America", bounds: [[-60, -90], [15, -30]] },
  { name: "Europe", bounds: [[35, -20], [70, 40]] },
  { name: "Africa", bounds: [[-35, -20], [38, 55]] },
  { name: "Asia", bounds: [[10, 50], [75, 180]] },
  { name: "Oceania", bounds: [[-50, 110], [0, 180]] },
  { name: "Antarctica", bounds: [[-90, -80], [-60, 40]] },
] as const;

const OCEANS = [
  { name: "Pacific", bounds: [[-60, -180], [60, -100]], x: 10, y: 80 },
  { name: "Atlantic", bounds: [[-60, -80], [60, 20]], x: 125, y: 80 },
  { name: "Indian", bounds: [[-60, 20], [30, 110]], x: 235, y: 100 },
  { name: "Pacific", bounds: [[-60, 100], [60, 180]], x: 340, y: 80 },
  { name: "Arctic", bounds: [[65, -80], [90, 40]], x: 180, y: 10 },
  { name: "Southern", bounds: [[-90, -20], [-50, 100]], x: 180, y: 170 },
] as const;

const GeoMinimap = (
  { onFitBounds, onResetView }: {
    onFitBounds: (bounds: [[number, number], [number, number]]) => void;
    onResetView: () => void;
  },
) => {
  const [paths, setPaths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch("/world.geojson")
      .then((res) => res.json())
      .then((data) => {
        const newPaths: string[] = [];

        // Simple Equirectangular Projection
        const project = (lng: number, lat: number) => {
          const x = lng + 180;
          const y = 90 - lat;
          return [x, y];
        };

        if (data && data.features) {
          data.features.forEach((f: any) => {
            const processCoords = (coords: any[]) => {
              let d = "";
              coords.forEach((ring: any[]) => {
                // Basic validation
                if (!Array.isArray(ring) || ring.length < 3) return;

                ring.forEach((pt: number[], i: number) => {
                  if (!Array.isArray(pt) || pt.length < 2) return;
                  const [px, py] = project(pt[0], pt[1]);
                  if (isNaN(px) || isNaN(py)) return;
                  d += (i === 0 ? "M" : "L") +
                    `${px.toFixed(1)} ${py.toFixed(1)} `;
                });
                d += "Z ";
              });
              return d;
            };

            const geom = f.geometry;
            if (geom.type === "Polygon") {
              newPaths.push(processCoords(geom.coordinates));
            } else if (geom.type === "MultiPolygon") {
              geom.coordinates.forEach((poly: any[]) => {
                newPaths.push(processCoords(poly));
              });
            }
          });
        }
        setPaths(newPaths);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load minimap data", err);
        setLoading(false);
      });
  }, []);

  return (
    <div class="absolute bottom-4 left-4 z-40 w-64 md:w-80 group hover:scale-105 transition-transform origin-bottom-left">
      <div class="relative w-full aspect-[2/1] bg-sky-900 border border-white overflow-hidden shadow-2xl">
        {/* Simplified World Map */}
        <svg
          viewBox="0 0 360 180"
          class="absolute inset-0 w-full h-full pointer-events-none"
        >
          {paths.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="#64748b"
              stroke="none"
              class="opacity-80"
            />
          ))}
        </svg>

        {/* Interactive Layer (Continents & Oceans) */}
        <div class="absolute inset-0 w-full h-full">
          <svg viewBox="0 0 360 180" class="w-full h-full cursor-pointer">
            {CONTINENTS.map((cont, i) => {
              const [[minLat, minLng], [maxLat, maxLng]] = cont.bounds;
              const x1 = minLng + 180;
              const y1 = 90 - maxLat;
              const x2 = maxLng + 180;
              const y2 = 90 - minLat;
              const w = x2 - x1;
              const h = y2 - y1;

              return (
                <rect
                  key={i}
                  x={x1}
                  y={y1}
                  width={w}
                  height={h}
                  fill="transparent"
                  class="hover:fill-white/10 transition-colors cursor-pointer"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onFitBounds(cont.bounds as any);
                  }}
                >
                  <title>{cont.name}</title>
                </rect>
              );
            })}
            {OCEANS.map((ocean, i) => (
              <text
                key={`ocean-${i}`}
                x={ocean.x}
                y={ocean.y}
                fontSize="12"
                textAnchor="middle"
                fill="currentColor"
                fontWeight="bold"
                class="text-white/30 hover:text-white cursor-pointer select-none transition-colors"
                onClick={(e: any) => {
                  e.stopPropagation();
                  onFitBounds(ocean.bounds as any);
                }}
              >
                {ocean.name[0]}
              </text>
            ))}
          </svg>
        </div>

        {/* Reset View Button */}
        <button
          type="button"
          class="absolute top-1 right-1 btn btn-xs btn-ghost text-white opacity-50 hover:opacity-100 p-0 min-h-0 h-6 w-6 rounded-full"
          onClick={(e: any) => {
            e.stopPropagation();
            onResetView();
          }}
          title="Reset View"
        >
          🌍
        </button>
      </div>
    </div>
  );
};

interface GeoPuzzleMapProps {
  onPlacePiece: (countryId: string) => void;
  hardMode: boolean;
  useHighRes: boolean;
  showMap: boolean;
  feedbackActive: { targetIso2: string; targetName: string } | null;
  currentWrongGuesses: Record<string, string>;
  setMapReady: (ready: boolean) => void;
  mapReady: boolean; // needed for dependency checks
}

export const GeoPuzzleMap = ({
  onPlacePiece,
  hardMode,
  useHighRes,
  showMap,
  feedbackActive,
  currentWrongGuesses,
  setMapReady,
  mapReady,
}: GeoPuzzleMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<MapControl | null>(null);

  // Initialization Effect
  useEffect(() => {
    if (!mapRef.current) return;

    let attempts = 0;
    const maxAttempts = 100; // 10 seconds

    const init = () => {
      if (!mapRef.current) return false;

      const global = globalThis as unknown as {
        initGeoPuzzle?: (
          c: HTMLElement,
          cb: (id: string) => void,
          hm: boolean,
          hr: boolean,
        ) => MapControl;
      };

      if (global.initGeoPuzzle) {
        try {
          // Prevent double-init
          if (controlRef.current) return true;

          const ctrl = global.initGeoPuzzle(
            mapRef.current,
            onPlacePiece,
            hardMode,
            useHighRes,
          );
          ctrl.setUseHighRes(useHighRes); // Initialize state immediately
          controlRef.current = ctrl;
          setMapReady(true);
          return true;
        } catch (e) {
          console.warn("[GeoPuzzleMap] Init failed", e);
          return false;
        }
      }
      return false;
    };

    if (init()) return;

    const interval = setInterval(() => {
      attempts++;
      if (init()) {
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        // Silent timeout
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (controlRef.current) {
        try {
          controlRef.current.destroy?.();
        } catch (e) {
          console.warn("Destroy error", e);
        }
        controlRef.current = null;
        setMapReady(false);
      }
    };
  }, []); // Run once on mount

  // Hard Mode Sync
  useEffect(() => {
    controlRef.current?.setHardMode(hardMode);
  }, [hardMode]);

  // High Res Sync
  useEffect(() => {
    controlRef.current?.setUseHighRes(useHighRes);
  }, [useHighRes]);

  // Feedback Sync
  useEffect(() => {
    if (!controlRef.current) return;

    controlRef.current.resetHighlights();

    if (feedbackActive) {
      Object.entries(currentWrongGuesses).forEach(([iso, name]) => {
        controlRef.current?.highlightCountryWithLabel(
          iso,
          name,
          "#991b1b",
          "guess",
        );
      });
      controlRef.current.highlightCountryWithLabel(
        feedbackActive.targetIso2,
        feedbackActive.targetName,
        "#166534",
        "target",
      );
    }
  }, [feedbackActive, currentWrongGuesses, mapReady]);

  return (
    <>
      <div
        ref={mapRef}
        id="map"
        class={`absolute inset-0 z-0 ${!showMap ? "invisible" : ""}`}
      />
      {/* Off-screen/Zoomed-out Target Indicator */}
      {feedbackActive && controlRef.current && (
        <div class="absolute inset-0 pointer-events-none z-30">
          {(() => {
            const center = controlRef.current?.getCountryCenter(
              feedbackActive.targetIso2,
            );
            const mapBounds = controlRef.current?.getBounds();

            let arrow = "→";
            let isVisible = false;

            if (!center) {
              return (
                <button
                  type="button"
                  onClick={() => {
                    // Auto-solve if map data is missing
                    onPlacePiece(feedbackActive.targetIso2);
                  }}
                  class="absolute bottom-12 left-1/2 transform -translate-x-1/2 btn btn-warning text-black px-6 py-2 rounded-full font-black text-sm shadow-2xl flex items-center gap-3 border-4 border-white pointer-events-auto hover:scale-105 transition-transform"
                >
                  <span>⚠️</span>
                  <span>
                    MAP DATA MISSING FOR {feedbackActive.targetName}
                  </span>
                  <span class="text-[10px] opacity-70">
                    (CLICK TO SKIP)
                  </span>
                </button>
              );
            }

            if (mapBounds) {
              // Simple bounding box check
              const lat = center[0];
              const lng = center[1];
              const southWest = mapBounds.getSouthWest();
              const northEast = mapBounds.getNorthEast();

              isVisible = lat >= southWest.lat && lat <= northEast.lat &&
                lng >= southWest.lng && lng <= northEast.lng;

              if (!isVisible) {
                const mapCenter = mapBounds.getCenter();
                const latDiff = center[0] - mapCenter.lat;
                const lngDiff = center[1] - mapCenter.lng;
                if (Math.abs(latDiff) > Math.abs(lngDiff)) {
                  arrow = latDiff > 0 ? "↑" : "↓";
                } else {
                  arrow = lngDiff > 0 ? "→" : "←";
                }
              }
            }

            return (
              <button
                type="button"
                onClick={() => {
                  if (controlRef.current) {
                    controlRef.current.panToCountry(feedbackActive.targetIso2);
                  }
                }}
                class="absolute bottom-12 left-1/2 transform -translate-x-1/2 btn btn-error text-white px-6 py-2 rounded-full font-black text-sm shadow-2xl flex items-center gap-3 border-4 border-white pointer-events-auto hover:scale-105 transition-transform"
              >
                <span>{isVisible ? "📍" : arrow}</span>
                <span>
                  {feedbackActive.targetName}{" "}
                  {isVisible ? "IS HERE!" : "IS THIS WAY!"}
                </span>
                <span class="text-[10px] opacity-70">
                  (CLICK TO {isVisible ? "FIND" : "PAN"})
                </span>
              </button>
            );
          })()}
        </div>
      )}

      {showMap && (
        <GeoMinimap
          onFitBounds={(bounds) => {
            if (controlRef.current) {
              controlRef.current.fitBounds(bounds);
            }
          }}
          onResetView={() => controlRef.current?.resetView()}
        />
      )}
    </>
  );
};
